import React, {
  ComponentProps,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import * as api from 'config/api';

import HighchartsReact from 'highcharts-react-official';
import * as Highcharts from 'highcharts';
import highchartsStock from 'highcharts/modules/stock';
import svgToDataURL from 'svg-to-dataurl';
import { SameDate } from 'utils/comparers';
import { CashMargin, Side } from 'config/consts';
import { PriceProps } from 'pages/TradeDetail';
import Loading from 'components/commons/loading/Loading';
import { commaFormat } from '../new-report/utils/formatter';
import { usePreviewPager } from '../home/PortfolioStuffing/asset/PortfolioStuffingAssetChart';
import { ChartRange, logoStyle } from '../preview-share/PreviewUtils';
import {
  StateController as StateControllerType,
  UsePreviewProps,
  usePreview,
} from '../preview-share/Preview';
import {
  ChartForPreviewInternalProps,
  ChartForPreviewType,
  RenderedChartToElementType,
  deconstructForPreviewSetter,
} from '../preview-share/useChartRenderer';
import {
  highchartsNavigatorMaskOptions,
  highchartsStockHandlesOptions,
  marker,
} from '../utils/chartUtils';

highchartsStock(Highcharts);

// blue-500
const mainLineColor = '#3b82f6';

type SymbolChartProps = {
  prices: Array<PriceProps> | undefined;
  orders: Array<api.GetOrdersResultItem>;
  toPreview: (element: React.ReactElement) => void;
  backHome: () => void;
  code: string;
  name: string;
};

const StateController: StateControllerType<Record<string, unknown>> = props => {
  return <></>;
};

const Renderer: RenderedChartToElementType<Record<string, unknown>> = ({
  range: chartRange,
  renderedChart,
  title,
  // eslint-disable-next-line no-empty-pattern
  state: {},
  width,
}) => {
  return (
    <>
      <div
        className="flex flex-col bg-white pb-5"
        style={width ? { width: `${width}px` } : {}}>
        <div className="flex flex-row items-center">
          <img
            src="/static/images/minkabu_assetplanner.png"
            alt=""
            style={logoStyle}
          />
          <ChartRange {...{ chartRange }} />
        </div>
        <div className="font-bold pb-3 text-lg ml-4">{title}</div>
        {renderedChart}
      </div>
    </>
  );
};

const SymbolChart: React.FC<SymbolChartProps> = ({
  prices,
  orders,
  toPreview,
  backHome,
  code,
  name,
}) => {
  const getOrderOneDay = (
    orders: api.GetOrdersResultItem[],
    price: api.GetSymbolResultPrice
  ): api.GetOrdersResultItem[] =>
    orders.filter(order => SameDate(order.TradeTime, price.Day));

  const getStockBuy = (
    orderOneDay: api.GetOrdersResultItem[]
  ): number | null => {
    return (
      orderOneDay.find(
        order => order.CashMargin === CashMargin.現物 && order.Side === Side.買
      )?.Price ?? null
    );
  };

  const getStockSell = (
    orderOneDay: api.GetOrdersResultItem[]
  ): number | null => {
    return (
      orderOneDay.find(
        order => order.CashMargin === CashMargin.現物 && order.Side === Side.売
      )?.Price ?? null
    );
  };

  const getMarginBuy = (
    orderOneDay: api.GetOrdersResultItem[]
  ): number | null => {
    return (
      orderOneDay.find(
        order => order.CashMargin === CashMargin.信用 && order.Side === Side.買
      )?.Price ?? null
    );
  };

  const getMarginSell = (
    orderOneDay: api.GetOrdersResultItem[]
  ): number | null => {
    return (
      orderOneDay.find(
        order => order.CashMargin === CashMargin.信用 && order.Side === Side.売
      )?.Price ?? null
    );
  };

  const getDateFromPrice = (price: api.GetSymbolResultPrice): Date => {
    return new Date(price.Day.replace(/-/g, '/'));
  };
  const getListItem = (
    price: PriceProps,
    stockBuy: number | null,
    stockSell: number | null,
    marginBuy: number | null,
    marginSell: number | null
  ): [
    Date,
    number,
    number | null,
    number | null,
    number | null,
    number | null
  ] => {
    return [
      getDateFromPrice(price),
      price.Close,
      stockBuy,
      stockSell,
      marginBuy,
      marginSell,
    ];
  };

  const list = useMemo(() => {
    return prices
      ?.sort(
        (a, b) => getDateFromPrice(b).getTime() - getDateFromPrice(a).getTime()
      )
      .map((price, index) => {
        const orderOneDay: api.GetOrdersResultItem[] = getOrderOneDay(
          orders,
          price
        );
        const stockBuy = getStockBuy(orderOneDay);
        const stockSell = getStockSell(orderOneDay);
        const marginBuy = getMarginBuy(orderOneDay);
        const marginSell = getMarginSell(orderOneDay);

        const result = getListItem(
          price,
          stockBuy,
          stockSell,
          marginBuy,
          marginSell
        );
        return result;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices, orders]);

  const props: UsePreviewProps<Record<string, unknown>, typeof list> = {
    Chart: SymbolChartForPreview,
    defaultState: {},
    extra: list,
    RenderedChartToElement: Renderer,
    StateController,
    stateToOptions: () => ({}),
    onCancel: backHome,
    chartTitle: `${code} ${name}`,
  };
  const { preview, PreviewButton, setRange } = usePreview(props);
  usePreviewPager(preview(), backHome, toPreview);

  const isChartDataEmpty = !list || list.length === 0;
  const isLoading = isChartDataEmpty;
  if (isLoading)
    return (
      <Loading
        loading={isLoading}
        circularLoading={true}
        style={{
          height: '350px',
          backgroundColor: '#edf2f7',
        }}
      />
    );

  return (
    <>
      <div className="-mr-3">
        <div className="relative z-10">
          <PreviewButton className="absolute top-0 right-0 ml-auto mt-2" />
        </div>
      </div>
      <HighchartsWrapper
        data={list}
        forPreviewSetter={{ tag: 'forGetRange', setRange }}
      />
    </>
  );
};

type ChartWrapperProps = {
  data:
    | [
        Date,
        number,
        number | null,
        number | null,
        number | null,
        number | null
      ][]
    | undefined;
};

const arrangeChartData = (
  l: [
    Date,
    number,
    number | null,
    number | null,
    number | null,
    number | null
  ][]
): Highcharts.SeriesOptionsType[] => {
  const list = l.sort((a, b) => a[0].getTime() - b[0].getTime());

  return [
    {
      type: 'area',
      name: '終値',
      turboThreshold: 15000,
      showInLegend: false,
      color: mainLineColor,
      fillOpacity: 0.1,
      lineWidth: 1,
      data: list.map(r => {
        return {
          name: '終値',
          x: r[0].getTime(),
          y: r[1],
        };
      }),
    },
    {
      type: 'line',
      name: '買/現物',
      lineWidth: 0,
      color: '#F2C14B',
      marker: {
        fillColor: '#F2C14B',
        enabled: true,
        radius: 6,
        symbol: 'triangle',
      },

      data: list.flatMap(r => {
        if (!r[2]) return [];
        return [
          {
            name: '買/現物',
            x: r[0].getTime(),
            y: r[2],
          },
        ];
      }),
    },
    {
      type: 'line',
      name: '売/現物',
      lineWidth: 0,
      color: '#845EEE',
      marker: {
        fillColor: '#845EEE',
        enabled: true,
        radius: 6,
        symbol: 'triangle-down',
      },
      data: list.flatMap(r => {
        if (!r[3]) return [];
        return [
          {
            name: '売/現物',
            x: r[0].getTime(),
            y: r[3],
          },
        ];
      }),
    },
    {
      type: 'line',
      name: '買/信用',
      lineWidth: 0,
      color: '#EE675E',
      marker: {
        fillColor: '#EE675E',
        enabled: true,
        radius: 6,
        symbol: 'triangle',
      },
      data: list.flatMap(r => {
        if (!r[4]) return [];
        return [
          {
            name: '買/信用',
            x: r[0].getTime(),
            y: r[4],
          },
        ];
      }),
    },
    {
      type: 'line',
      name: '売/信用',
      lineWidth: 0,
      color: '#5ECBEE',
      marker: {
        fillColor: '#5ECBEE',
        enabled: true,
        radius: 6,
        symbol: 'triangle-down',
      },
      data: list.flatMap(r => {
        if (!r[5]) return [];
        return [
          {
            name: '売/信用',
            x: r[0].getTime(),
            y: r[5],
          },
        ];
      }),
    },
  ];
};

const apply = <T, S>(f: (value: T) => S) => (
  value: T | undefined | null
): S | undefined => {
  if (value !== null && value !== undefined) return f(value);
};

const buttonToDateRange = (
  button: Highcharts.RangeSelectorButtonsOptions
): number | null => {
  if (!button.count) return null;

  const multiplier = (button.type === 'year' ? 12 : 1) * 30;
  return button.type === 'month' || button.type === 'year'
    ? button.count * multiplier
    : null;
};

const calcRangeFromButtons = (
  list: [
    Date,
    number,
    number | null,
    number | null,
    number | null,
    number | null
  ][],
  buttons: Highcharts.RangeSelectorButtonsOptions[]
): Highcharts.RangeSelectorButtonsOptions | null => {
  const latest = getLatestOrderDate(list);
  if (!latest) return null;
  const button = buttons
    .flatMap(v =>
      buttonToDateRange(v)
        ? [{ value: buttonToDateRange(v) as number, button: v }]
        : []
    )
    .sort((a, b) => a.value - b.value) // 期間が小さい順
    .reduce((acc, v) => {
      if (acc) return acc;
      return new Date(new Date().getTime() - v.value * 24 * 60 * 60 * 1000) <=
        latest
        ? v.button
        : null;
    }, null as Highcharts.RangeSelectorButtonsOptions | null);
  return button;
};

const getLatestOrderDate = (
  list: [
    Date,
    number,
    number | null,
    number | null,
    number | null,
    number | null
  ][]
): Date | null => {
  const latest = list
    .sort((a, b) => a[0].getTime() - b[0].getTime())
    .filter(v => v[2] !== null || v[3] !== null)[0];
  return latest?.[0] ?? null;
};

const HighchartsWrapper = ({
  data: list,
  options: options_,
  forPreviewSetter,
}: ChartWrapperProps & ChartForPreviewInternalProps) => {
  const { setChartDataURL, setRange } = deconstructForPreviewSetter(
    forPreviewSetter
  );
  const backupButton: Highcharts.RangeSelectorButtonsOptions = {
    type: 'all',
    text: '全期間',
    count: 6,
  };
  const buttons: Highcharts.RangeSelectorButtonsOptions[] = [
    { type: 'month', text: '1ヶ月', count: 1 },
    { type: 'month', text: '3ヶ月', count: 3 },
    { type: 'month', text: '6ヶ月', count: 6 },
    { type: 'year', text: '1年', count: 1 },
    backupButton,
  ];
  const defaultButton = list
    ? calcRangeFromButtons(list, buttons) ?? backupButton
    : backupButton;
  const millisecondRange = apply(
    (value: number) => value * 24 * 60 * 60 * 1000
  )(buttonToDateRange(defaultButton));
  const [options, setOptions] = useState<Highcharts.Options>({
    exporting: { enabled: false },
    legend: {
      enabled: true,
      align: 'center',
      verticalAlign: 'top',
      itemDistance: 7,
      useHTML: true,
      itemStyle: {
        fontSize: '10px',
        fontWeight: 'normal',
        color: '#000000',
      },
      margin: 4,
      y: -8,
    },
    navigator: {
      xAxis: {
        left: 9,
        width: '100%',
      },
      ...highchartsStockHandlesOptions,
      ...highchartsNavigatorMaskOptions,
    },
    xAxis: {
      crosshair: { color: '#8e9db2' },
      type: 'datetime',
      labels: {
        formatter: function(): string {
          const date = new Date(this.value);
          return `${date.getFullYear()}/${date.getMonth() +
            1}/${date.getDate().toString()}`;
        },
        style: {
          fontSize: '9px',
        },
      },
      tickLength: 0,
      range: millisecondRange,
      events: {
        afterSetExtremes: function(e): void {
          if (e.trigger === 'rangeSelectorButton') {
            const diff = e.dataMax - e.max;
            this.setExtremes(e.min + diff, e.dataMax, true);
          }
        },
        setExtremes: function(e): void {
          setRange &&
            setRange(
              (x => ({
                min: new Date(x.min),
                max: new Date(x.max),
              }))(e)
            );
        },
      },
    },
    yAxis: {
      opposite: true,
      labels: {
        formatter: function(): string {
          return `${
            typeof this.value === 'number'
              ? commaFormat(this.value, { numberOfDecimals: 0 })
              : ''
          }`;
        },
        style: { fontSize: '9px' },
        align: 'left',
        x: 0,
      },
    },
    scrollbar: { enabled: false },
    credits: { enabled: false },
    rangeSelector: {
      x: 10,
      dropdown: 'always',
      buttonTheme: {
        fill: 'none',
        stroke: 'none',
        'stroke-width': 0,
        r: 6,
        width: 40,
        height: 20,
        style: {
          textAlign: 'center',
          color: '#8e9db2',
          fontWeight: 'bold',
        },
        states: {
          hover: {
            fill: 'white',
            style: {
              color: '#334155',
              fontWeight: 'normal',
              fontSize: '12px',
            },
          },
          select: {
            fill: 'white',
            style: {
              color: '#334155',
              fontWeight: 'normal',
              fontSize: '12px',
            },
          },
        },
      },
      buttons,
      buttonPosition: {
        align: 'left',
        x: -20,
      },
      inputEnabled: false,
    },
    chart: {
      height: 350,
      marginBottom: 20,
    },
    subtitle: {
      text: '',
    },
    title: {
      text: '',
    },
    tooltip: {
      useHTML: true,
      outside: true,
      shared: true,
      borderColor: 'transparent',
      formatter: function(): string {
        const Tooltip = ({
          date,
          children,
        }: {
          date: Date;
          children: React.ReactNodeArray;
        }): React.ReactElement => {
          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gap: '2px',
                  gridAutoRows: 'min-content',
                  gridAutoFlow: 'row',
                }}>
                <div style={{ paddingBottom: '3px', fontSize: '9px' }}>
                  {date.getFullYear()}/{date.getMonth() + 1}/{date.getDate()}
                </div>
                {children}
              </div>
            </>
          );
        };
        const TooltipItem = ({
          name,
          color,
          value,
        }: {
          name: string;
          color: string;
          value: number;
        }): React.ReactElement => {
          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'min-content min-content min-content',
                  placeItems: 'center start',
                  gap: '2px',
                }}>
                <div
                  style={{
                    borderRadius: '50%',
                    height: '5px',
                    width: '5px',
                    gridColumn: 1,
                    backgroundColor: color,
                  }}
                />
                <span style={{ fontSize: '10px', gridColumn: 2 }}>{name}:</span>
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: '10px',
                    gridColumn: 3,
                  }}>
                  {commaFormat(value, { numberOfDecimals: 1 })}
                </span>
              </div>
            </>
          );
        };
        return renderToStaticMarkup(
          <Tooltip date={new Date(this.x)}>
            {(this.points ?? []).map(point => (
              <TooltipItem
                name={point.series.name}
                color={point.color.toString()}
                value={point.y}
              />
            ))}
          </Tooltip>
        );
      },
    },
    plotOptions: {
      series: {
        events: { legendItemClick: (): boolean => false },
        softThreshold: false,
        turboThreshold: 15000,
        dataGrouping: {
          enabled: false,
        },
        ...marker,
      },
    },
  });

  useEffect(() => {
    setOptions(prev => {
      return (options_
        ? (x: Highcharts.Options) => Highcharts.merge(x, options_)
        : (x: Highcharts.Options) => x)({
        ...prev,
        series: apply(arrangeChartData)(list),
      });
    });
  }, [list, options_]);

  useEffect(() => {
    setChartDataURL &&
      ref.current &&
      setChartDataURL(svgToDataURL(ref.current.chart.getSVG()));
    setRange &&
      ref.current &&
      setRange(
        (ext => ({ max: new Date(ext.max), min: new Date(ext.min) }))(
          ref.current.chart.xAxis[0].getExtremes()
        )
      );
  }, [options, setChartDataURL, setRange]);
  const ref = useRef<HighchartsReact.RefObject>(null);

  if (!list) return <></>;

  return (
    <>
      <HighchartsReact
        constructorType="stockChart"
        highcharts={Highcharts}
        options={options}
        ref={ref}
      />
    </>
  );
};

export const SymbolChartForPreview: (
  props: ComponentProps<ChartForPreviewType<ChartWrapperProps['data']>>
) => React.ReactElement = ({ data, options, range, setChartDataURL }) => {
  return (
    <>
      <HighchartsWrapper
        {...{
          data,
          options: range
            ? Highcharts.merge(options, {
                xAxis: { min: range.min.getTime(), max: range.max.getTime() },
                legend: {
                  verticalAlign: 'top',
                  align: 'left',
                  itemDistance: 8,
                },
              } as Highcharts.Options)
            : options,
          forPreviewSetter: {
            tag: 'forInPreview',
            setChartDataURL,
          },
        }}
      />
    </>
  );
};

export default SymbolChart;
